/**
 * @file Derives the display value and color for an investor's accreditation status.
 */
import type { Accreditation } from "../types";

export const ACCREDITATION_VALUE_NO = "No";
export const ACCREDITATION_VALUE_YES = "Yes";
export const ACCREDITATION_VALUE_UNVERIFIED = "Unverified";

const NO: Accreditation = { value: ACCREDITATION_VALUE_NO, color: "red" };
const YES: Accreditation = { value: ACCREDITATION_VALUE_YES, color: "green" };
const UNVERIFIED: Accreditation = { value: ACCREDITATION_VALUE_UNVERIFIED, color: "orange" };

/**
 * Determines whether an investor counts as accredited, unverified or not accredited for export.
 */
const getAccreditation = (
  hasAccreditation?: boolean,
  accreditationExpiresAt?: string,
  projectCloseDate?: string,
  isFunded = false,
  isNonFund = true,
): Accreditation => {
  if (isNonFund) {
    const accreditation = hasAccreditation
      ? YES
      : NO;

    return accreditation;
  }

  if (!hasAccreditation) {
    return NO;
  }

  if (!accreditationExpiresAt) {
    return UNVERIFIED;
  }

  if (isFunded) {
    return YES;
  }

  const todayTime = new Date().getTime();
  const accreditationExpiresTime = Date.parse(accreditationExpiresAt);

  const isExpired = todayTime > accreditationExpiresTime;

  if (!isExpired) {
    return YES;
  }

  const projectCloseTime = Date.parse(projectCloseDate);

  const hasExpiredAfterProjectClose = accreditationExpiresTime > projectCloseTime;

  if (hasExpiredAfterProjectClose) {
    return YES;
  }

  return UNVERIFIED;
};

export default getAccreditation;
